import { BuilderElement } from "@/types/elements";
import { configStore, documentStore } from "@/store/store-accessor";
import { TreeNode } from "@/types/tree";
import { findNode } from "@/lib/tree/tree";
import { SnapshotLabelAction } from "@/types/snapshot";
import { generateSnapshotLabel } from "@/lib/undo/snapshot-labels/utils";
import { getElementControlWithinTreeWithSplittedPathByElementControlPathWithPrefix } from "@/lib/elements/utils";
import { getControlLabelWithAncestorsLabelsFirst } from "@/lib/controls/utils";
import { MISSING_ELEMENT_SLUG } from "@/lib/elements/constants";

export const DEFAULT_ELEMENT_NAME = "element";
export const DEFAULT_CHANGED_PROPERTY_NAME = "property";

export const generateDocumentStorePropertyChangeSnapshotLabel = {
  forElementByTreeNodeId: (
    treeNodeId: TreeNode["id"],
    changedDocumentStorePropertyPath: string,
    snapshotLabelAction: SnapshotLabelAction
  ): string => {
    const maybeNode = findNode(documentStore.document.tree, treeNodeId);

    const element = configStore.element(
      maybeNode?.data.type || MISSING_ELEMENT_SLUG
    );

    return generateDocumentStorePropertyChangeSnapshotLabel.forElementBasingOnChangedStorePropertyPath(
      element,
      changedDocumentStorePropertyPath,
      snapshotLabelAction
    );
  },
  forElementBasingOnChangedStorePropertyPath: (
    element: BuilderElement,
    changedDocumentStorePropertyPath: string,
    snapshotLabelAction: SnapshotLabelAction
  ): string => {
    const maybeControlWithinTreeAndParsedPath =
      getElementControlWithinTreeWithSplittedPathByElementControlPathWithPrefix(
        element,
        changedDocumentStorePropertyPath
      );

    const additionalSnapshotLabelParts = [];

    if (maybeControlWithinTreeAndParsedPath) {
      additionalSnapshotLabelParts.push(
        ...getControlLabelWithAncestorsLabelsFirst(
          maybeControlWithinTreeAndParsedPath
        )
      );
    } else {
      additionalSnapshotLabelParts.push(DEFAULT_CHANGED_PROPERTY_NAME);
    }

    return generateSnapshotLabel(
      {
        action: snapshotLabelAction,
        subject: element.name
      },
      additionalSnapshotLabelParts
    );
  }
};

export function getBuilderElementNameByTreeNodeId(
  treeNodeId: TreeNode["id"]
): string {
  const maybeNode = findNode(documentStore.document.tree, treeNodeId);

  return getBuilderElementNameBySlug(maybeNode?.data.type);
}

export function getBuilderElementNameBySlug(
  slug?: BuilderElement["slug"]
): string {
  if (!slug) return DEFAULT_ELEMENT_NAME;

  const element = configStore.element(slug);

  return element.name;
}
